import React, { useState, useContext, useEffect } from 'react';
import { 
  FaBuilding,  
  FaVideo, 
  FaTimes, 
  FaChevronLeft, 
  FaChevronRight,
  FaMosque,
  FaSchool,
  FaWater,
  FaHospital,
  FaCogs,
  FaSpinner
} from 'react-icons/fa';
import { constructionImages } from '../data/constructionImages';
import '../styles/ConstructionGallery.css';
import '../styles/LazyLoad.css';
import '../media-query/consgalley.css';
import { LanguageContext } from '../context/LanguageContext';

const ConstructionGallery = () => {
  const [activeCategory, setActiveCategory] = useState('buildings');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const { translations } = useContext(LanguageContext);

  const t = translations.ConstructionGallery;

  const galleryData = {
    buildings: {
      title: t.galleryTitles?.buildings || 'Omer Farooq Masjid',
      icon: <FaMosque />,
      images: constructionImages.buildings?.images || [],
    },
    infrastructure: {
      title: t.galleryTitles?.infrastructure || 'New Ansarullah School',
      icon: <FaSchool />,
      images: constructionImages.infrastructure?.images || [],
    },
    interior: {
      title: t.galleryTitles?.interior || 'Abu-Bakr Siddiq Masjid',
      icon: <FaMosque />,
      images: constructionImages.interior?.images || [],
    },
    interior2: {
      title: t.galleryTitles?.interior2 || 'Ansarullah School Work Flow',
      icon: <FaCogs />,
      images: constructionImages.interior2?.images || [],
    },
    heavy: {
      title: t.galleryTitles?.heavy || 'Water Supply Network',
      icon: <FaWater />,
      images: constructionImages.heavy?.images || [],
    },
    heavy2: {
      title: t.galleryTitles?.heavy2 || 'Saidabad District Clinic',
      icon: <FaHospital />,
      images: constructionImages.heavy2?.images || [],
    },
    videos: {
      title: t.galleryTitles?.videos || 'Construction Videos',
      icon: <FaVideo />,
      images: [],
      videos: [],
    },
  };

  const categories = [
    { key: 'buildings', icon: <FaMosque /> },
    { key: 'infrastructure', icon: <FaSchool /> },
    { key: 'interior', icon: <FaMosque /> },
    { key: 'interior2', icon: <FaCogs /> },
    { key: 'heavy', icon: <FaWater /> },
    { key: 'heavy2', icon: <FaHospital /> },
    { key: 'videos', icon: <FaVideo /> },
  ];

  const currentContent = galleryData[activeCategory];

  // Show skeleton only briefly, then show images
  useEffect(() => {
    setShowSkeleton(true);
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [activeCategory]);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrev = () => {
    const images = currentContent.images;
    if (!images || images.length === 0) return;
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const goToNext = () => {
    const images = currentContent.images;
    if (!images || images.length === 0) return;
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrev();
    if (e.key === 'ArrowRight') goToNext();
  };

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, currentIndex]);

  // Get category label from translations - THIS IS THE FIX
  const getCategoryLabel = (key) => {
    // Use translations from the JSON file
    if (t.categories && t.categories[key]) {
      return t.categories[key];
    }
    // Fallback English labels
    const labels = {
      'buildings': 'Omer Farooq Masjid',
      'infrastructure': 'New Ansarullah School', 
      'interior': 'Abu-Bakr Siddiq Masjid',
      'interior2': 'Ansarullah Work Flow',
      'heavy': 'Water Supply Network',
      'heavy2': 'Saidabad Clinic',
      'videos': 'Construction Videos'
    };
    return labels[key] || key;
  };

  // Skeleton loader component
  const SkeletonLoader = ({ count = 6 }) => {
    return (
      <div className="construction-images-grid">
        {Array(count).fill(0).map((_, index) => (
          <div key={`skeleton-${index}`} className="construction-image-card skeleton-card">
            <div className="skeleton-image"></div>
            <div className="skeleton-shimmer"></div>
          </div>
        ))}
      </div>
    );
  };

  // Image component - shows immediately with placeholder
  const GalleryImage = ({ src, alt, onClick }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    return (
      <div className="construction-image-card" onClick={onClick}>
        {!loaded && !error && (
          <div className="image-placeholder">
            <FaSpinner className="spinner" />
          </div>
        )}
        {error && (
          <div className="image-error">
            <FaBuilding />
            <span>Failed to load</span>
          </div>
        )}
        <img 
          src={src} 
          alt={alt}
          className="construction-gallery-image"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          loading="lazy"
        />
      </div>
    );
  };

  return (
    <div className="construction-gallery">
      {/* Header */}
      <div className="construction-header">
        <div className="construction-header-left">
          <div className="construction-header-icon">
            <FaBuilding />
          </div>
          <h1 className="construction-header-title">{t.headerTitle || 'Construction Gallery'}</h1>
        </div>
      </div>

      <div className="construction-container">
        {/* Sidebar */}
        <div className="construction-sidebar">
          <h3 className="construction-sidebar-title">{t.sidebarTitle || 'Categories'}</h3>
          <ul className="construction-category-list">
            {categories.map((category) => (
              <li
                key={category.key}
                className={`construction-category-item ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                <span className="construction-category-icon">{category.icon}</span>
                <span className="construction-category-label">{getCategoryLabel(category.key)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Gallery */}
        <div className="construction-content">
          <div className="construction-content-header">
            <div className="construction-content-title">
              <span className="construction-title-icon">{currentContent.icon}</span>
              <h2>{currentContent.title}</h2>
              <span className="image-count">
                ({currentContent.images?.length || 0} {t.images || 'images'})
              </span>
            </div>
          </div>

          {activeCategory === 'videos' ? (
            // Video Gallery
            currentContent.videos && currentContent.videos.length > 0 ? (
              <div className="construction-videos-grid">
                {currentContent.videos.map((video) => (
                  <div key={video.id} className="construction-video-card">
                    <div className="video-container">
                      <iframe
                        src={video.src}
                        title={video.title}
                        className="construction-gallery-video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-content-message">
                <div className="no-content-icon">
                  <FaVideo />
                </div>
                <p>{t.noVideos || 'No videos available'}</p>
              </div>
            )
          ) : (
            // Image Gallery
            showSkeleton ? (
              <SkeletonLoader count={Math.min(currentContent.images?.length || 6, 6)} />
            ) : currentContent.images && currentContent.images.length > 0 ? (
              <div className="construction-images-grid">
                {currentContent.images.map((item, index) => (
                  <GalleryImage
                    key={item.id || index}
                    src={item.src}
                    alt={`${currentContent.title} - ${t.image || 'Image'} ${index + 1}`}
                    onClick={() => openLightbox(item, index)}
                  />
                ))}
              </div>
            ) : (
              <div className="no-content-message">
                <div className="no-content-icon">
                  {currentContent.icon}
                </div>
                <p>{t.noImages || 'No images available'}</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="close-lightbox" 
              onClick={closeLightbox}
              title={t.close || 'Close'}
              aria-label={t.close || 'Close'}
            >
              <FaTimes />
            </button>
            
            <img 
              src={selectedImage.src} 
              alt={t.fullView || 'Full view'} 
              className="lightbox-image"
              loading="lazy"
            />
            
            {currentContent.images && currentContent.images.length > 1 && (
              <div className="lightbox-nav">
                <button 
                  className="nav-btn" 
                  onClick={goToPrev}
                  title={t.prev || 'Previous'}
                  aria-label={t.prev || 'Previous'}
                >
                  <FaChevronLeft />
                </button>
                <button 
                  className="nav-btn" 
                  onClick={goToNext}
                  title={t.next || 'Next'}
                  aria-label={t.next || 'Next'}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConstructionGallery;